<template>
  <div class="ad-revenue-query">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <!-- 筛选条件 -->
      <el-form :inline="true" :model="form" label-width="80px" @submit.native.prevent>
        <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;">
          <el-form-item label="时间范围" class="date-range-item">
            <el-date-picker
              v-model="form.dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              :default-time="['00:00:00', '23:59:59']"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
              :clearable="false"
              style="width:380px;"
            />
          </el-form-item>
          <el-form-item label="用户ID">
            <el-input v-model.trim="form.userId" clearable placeholder="请输入用户ID" style="width:140px;" />
          </el-form-item>
          <el-form-item label="广告平台">
            <el-select v-model="form.adnName" clearable filterable placeholder="全部平台" style="width:160px;">
              <el-option
                v-for="item in adnOptions"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="广告类型">
            <el-select v-model="form.adType" clearable filterable placeholder="全部类型" style="width:150px;">
              <el-option
                v-for="item in adTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="loading" @click="handleSearch" icon="el-icon-search">查询</el-button>
            <el-button type="info" plain @click="handleReset" :disabled="loading" icon="el-icon-refresh">重置</el-button>
          </el-form-item>
        </div>
      </el-form>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && !hasTable && pagination.total === 0"
        description="所选时间范围内暂无广告记录，请扩大时间范围后重试"
      />

      <div v-else>
        <!-- 汇总卡片 -->
        <div class="summary-bar">
          <div class="summary-cards">
            <el-card class="summary-card card-total animate__animated animate__fadeInUp">
              <div class="summary-content">
                <i class="el-icon-wallet summary-icon"></i>
                <div class="summary-value">{{ totalEcpmDisplay }}</div>
                <div class="summary-label">总收益</div>
              </div>
            </el-card>
            <el-card
              v-for="(item, index) in adnSummaryList"
              :key="item.adnName"
              class="summary-card animate__animated animate__fadeInUp"
              :style="{ background: adnColors[index % adnColors.length], border: 'none', color: '#fff' }"
            >
              <div class="summary-content">
                <i class="el-icon-s-data summary-icon"></i>
                <div class="summary-value">{{ item.totalEcpm }}</div>
                <div class="summary-label">{{ item.adnName }}收益</div>
              </div>
            </el-card>
            <el-card class="summary-card card-count animate__animated animate__fadeInUp">
              <div class="summary-content">
                <i class="el-icon-document summary-icon"></i>
                <div class="summary-value">{{ totalRecordCount }}</div>
                <div class="summary-label">总记录数</div>
              </div>
            </el-card>
          </div>
        </div>

        <!-- 数据表格 -->
        <el-table
          :data="tableData"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在查询..."
          class="animate__animated animate__fadeIn"
          row-key="id"
        >
          <el-table-column prop="id" label="ID" width="80" align="center"/>
          <el-table-column prop="userId" label="用户ID" width="90" align="center"/>
          <el-table-column prop="adnName" label="广告平台" min-width="110" />
          <el-table-column prop="adType" label="广告类型" min-width="100" :formatter="adTypeFormatter" />
          <el-table-column prop="ecpm" label="收入" min-width="100" :formatter="ecpmFormatter" />
          <el-table-column prop="transId" label="交易ID" min-width="160" show-overflow-tooltip />
          <el-table-column prop="reason" label="原因" min-width="100" show-overflow-tooltip />
          <el-table-column
            prop="createdTime"
            label="创建时间"
            min-width="170"
            :formatter="timeFormatter"
          />
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { get } from '@/utils/http.js'
import { getAdPlatformSummary, getAdPlatforms } from '@/api/dashboard'

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatDateTime(date) {
  return [
    date.getFullYear(),
    pad2(date.getMonth() + 1),
    pad2(date.getDate())
  ].join('-') + ` ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
}

function getDefaultDateRange() {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
  return [formatDateTime(start), formatDateTime(end)]
}

function parsePageData(data) {
  if (!data) {
    return { records: [], total: 0, page: 1, pageSize: 10 }
  }
  const records = data.records || data.list || data.rows || []
  return {
    records: Array.isArray(records) ? records : [],
    total: Number(data.total ?? data.totalCount ?? 0),
    page: Number(data.page ?? data.current ?? data.pageNum ?? 1),
    pageSize: Number(data.pageSize ?? data.size ?? 10)
  }
}

export default {
  name: 'AdRevenueQuery',
  data() {
    return {
      form: {
        dateRange: getDefaultDateRange(),
        userId: '',
        adnName: '',
        adType: ''
      },
      adnOptions: [],
      adTypeOptions: [
        { label: 'banner', value: 'banner' },
        { label: '插屏', value: 'interstitial' },
        { label: '信息流', value: 'native_feed' },
        { label: '激励', value: 'reward' },
        { label: '开屏', value: 'splash' }
      ],
      loading: false,
      error: '',
      tableData: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      platformSummary: { totalRevenue: 0, totalCount: 0, items: [] },
      adnColors: [
        'linear-gradient(135deg, #E6A23C, #C78C2E)',
        'linear-gradient(135deg, #9B59B6, #7D3C98)',
        'linear-gradient(135deg, #20C997, #17A88A)',
        'linear-gradient(135deg, #F56C6C, #D9534F)',
        'linear-gradient(135deg, #00BFFF, #1E90FF)',
        'linear-gradient(135deg, #FF8C00, #E07B00)'
      ]
    }
  },
  computed: {
    isSuperAdmin() {
      return parseInt(localStorage.getItem('role')) === 0
    },
    hasTable() {
      return this.pagination.total > 0 || (Array.isArray(this.tableData) && this.tableData.length > 0)
    },
    totalEcpmDisplay() {
      const revenue = this.platformSummary.totalRevenue || 0
      return (revenue / 1000).toFixed(2)
    },
    totalRecordCount() {
      return this.platformSummary.totalCount || 0
    },
    adnSummaryList() {
      return (this.platformSummary.items || [])
        .map(item => ({
          adnName: item.adnName || '未知',
          totalEcpm: ((item.ecpm || 0) / 1000).toFixed(2),
          percent: item.percent || 0
        }))
    }
  },
  async mounted() {
    this.handleSearch()
    try {
      this.adnOptions = await getAdPlatforms() || []
    } catch (e) {
      console.warn('获取广告平台列表失败:', e)
    }
  },
  methods: {
    timeFormatter(row, column, cellValue) {
      if (!cellValue) return ''
      return String(cellValue).replace('T', ' ')
    },
    ecpmFormatter(row, column, cellValue) {
      return ((cellValue || 0) / 1000).toFixed(2) 
    },
    adTypeFormatter(row, column, cellValue) {
      const map = {
        banner: 'banner',
        interstitial: '插屏',
        native_feed: '信息流',
        reward: '激励',
        splash: '开屏'
      }
      return map[cellValue] || cellValue || ''
    },

    buildQueryParams(includePagination = true) {
      const [start, end] = this.form.dateRange || []
      if (!start || !end) {
        throw new Error('请选择时间范围')
      }
      if (new Date(end.replace(' ', 'T')) < new Date(start.replace(' ', 'T'))) {
        throw new Error('结束时间不能早于开始时间')
      }

      const params = {
        startTime: start,
        endTime: end
      }
      if (includePagination) {
        params.page = this.pagination.page
        params.pageSize = this.pagination.pageSize
      }
      if (this.form.userId) params.userId = Number(this.form.userId)
      if (this.form.adnName) params.adnName = this.form.adnName
      if (this.form.adType) params.adType = this.form.adType
      return params
    },

    applyPageResult(data) {
      const page = parsePageData(data)
      this.tableData = page.records
      this.pagination.total = page.total
      this.pagination.pageSize = page.pageSize || this.pagination.pageSize
      this.pagination.page = page.page || this.pagination.page
      return page
    },

    async fetchPlatformSummary() {
      try {
        const [start, end] = this.form.dateRange || []
        if (!start || !end) return
        const params = { startTime: start, endTime: end }
        if (this.form.userId) params.userId = Number(this.form.userId)
        if (this.form.adnName) params.adnName = this.form.adnName
        if (this.form.adType) params.adType = this.form.adType
        this.platformSummary = await getAdPlatformSummary(start, end, params)
      } catch (e) {
        this.platformSummary = { totalRevenue: 0, totalCount: 0, items: [] }
      }
    },

    handleReset() {
      this.form = {
        dateRange: getDefaultDateRange(),
        userId: '',
        adnName: '',
        adType: ''
      }
      this.tableData = []
      this.error = ''
      this.platformSummary = { totalRevenue: 0, totalCount: 0, items: [] }
      this.pagination = { page: 1, pageSize: 10, total: 0 }
      this.handleSearch()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchData()
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchData()
    },

    async handleSearch() {
      this.pagination.page = 1
      this.tableData = []
      this.platformSummary = { totalRevenue: 0, totalCount: 0, items: [] }
      this.fetchPlatformSummary()
      await this.fetchData()
    },

    async fetchData() {
      this.error = ''
      this.loading = true

      try {
        const params = this.buildQueryParams(true)
        const result = await get('/admin/adRecord/page', params)
        this.applyPageResult(result && result.data)
      } catch (e) {
        this.error = e && e.message ? e.message : '请求出现错误'
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.ad-revenue-query ::v-deep .el-table th,
.ad-revenue-query ::v-deep .el-table th.is-leaf {
  background-color: #D6E4FF;
}

.date-range-item {
  position: relative;
}

/* ===== 汇总卡片 ===== */
.summary-bar {
  margin-bottom: 30px;
}

.summary-cards {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.summary-cards .summary-card {
  flex: 1;
  min-width: 160px;
}

.summary-card {
  border-radius: 10px;
  overflow: hidden;
  transition: transform 0.3s ease;
  height: 100px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.05);
}

.summary-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.1);
}

.summary-card.card-total {
  background: linear-gradient(135deg, #409EFF, #337ECC);
  color: #fff;
  border: none;
}

.summary-card.card-count {
  background: linear-gradient(135deg, #67C23A, #529B2E);
  color: #fff;
  border: none;
}

.summary-content {
  text-align: center;
}

.summary-icon {
  font-size: 20px;
  margin-bottom: 4px;
  display: block;
  color: inherit;
}

.summary-value {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 2px;
}

.summary-label {
  font-size: 11px;
  opacity: 0.85;
  font-weight: normal;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.error {
  color: #f87171;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.el-card.animate__animated,
.el-table.animate__animated {
  --animate-duration: 0.3s;
}
</style>